import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Badge, Empty, AgentJsonBox, statusTone } from "@/components/admin/Ui";
import { Sparkle, CheckCircle, XCircle, UserPlus } from "@phosphor-icons/react";

const recTone = (r) => ({
  approve: "green", reject: "red", needs_manual_review: "amber", pending: "neutral"
}[r] || "neutral");

export default function Applications() {
  const [apps, setApps] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [onboardOutput, setOnboardOutput] = useState(null);

  const load = async () => {
    const { data } = await api.get("/applications");
    setApps(data.applications || []);
  };

  useEffect(() => { load(); }, []);

  const process = async (id) => {
    setBusy(true);
    setOnboardOutput(null);
    try {
      const { data } = await api.post("/applications/process", { application_id: id });
      toast.success(`AI: ${data.application.recommendation} (${data.application.ai_score}/10)`);
      await load();
      setSelected(data.application);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Process failed");
    } finally { setBusy(false); }
  };

  const setStatus = async (id, status) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/applications/${id}`, { status });
      await load();
      setSelected(data);
      toast.success(`Marked ${status}`);
    } catch (e) { toast.error("Update failed"); }
    finally { setBusy(false); }
  };

  const onboard = async (id) => {
    if (!window.confirm("Approve this applicant and create a member?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/members/onboard", { application_id: id });
      toast.success("Member onboarded");
      setOnboardOutput(data.onboarding);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Onboard failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-applications-page">
      <PageHeader title="Applications" subtitle="Agents 5 + 6 · GPT-5" />
      <div className="p-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-5 bg-white border border-black/10">
          <div className="px-5 py-3 border-b border-black/10 overline">Queue ({apps.length})</div>
          <ul className="divide-y divide-black/10 max-h-[70vh] overflow-y-auto">
            {apps.length === 0 && <li className="p-5"><Empty label="No applications submitted yet." /></li>}
            {apps.map((a) => (
              <li key={a.id}
                onClick={() => { setSelected(a); setOnboardOutput(null); }}
                data-testid={`application-item-${a.id}`}
                className={`px-5 py-4 cursor-pointer hover:bg-navy/5 ${selected?.id === a.id ? "bg-navy/5 border-l-2 border-ember" : ""}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium text-navy text-sm">{a.company_name}</div>
                  <Badge tone={statusTone(a.status)}>{a.status}</Badge>
                </div>
                <div className="text-xs text-navy/60 mt-1">{a.applicant_name} · {a.country}</div>
                <div className="flex items-center justify-between mt-2">
                  <Badge tone={recTone(a.recommendation)}>{a.recommendation}</Badge>
                  <span className="ship-number text-lg font-bold text-ember">{a.ai_score || "—"}</span>
                </div>
              </li>
            ))}
          </ul>
        </aside>

        <section className="lg:col-span-7">
          {!selected ? <Empty label="Select an application to review." /> : (
            <div className="bg-white border border-black/10 p-6 space-y-5" data-testid="application-detail">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="overline mb-1">Application</div>
                  <h3 className="font-display text-2xl font-bold text-navy">{selected.company_name}</h3>
                  <div className="text-sm text-navy/60 mt-1">{selected.applicant_name} · {selected.email}</div>
                </div>
                <div className="text-right">
                  <div className="ship-number text-4xl font-black text-ember leading-none">{selected.ai_score || "—"}</div>
                  <Badge tone={recTone(selected.recommendation)}>{selected.recommendation}</Badge>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <Detail k="Country" v={selected.country} />
                <Detail k="City" v={selected.city} />
                <Detail k="Website" v={selected.website || "—"} />
                <Detail k="Years" v={selected.years_in_business} />
                <Detail k="Employees" v={selected.employee_count || "—"} />
                <Detail k="Turnover" v={selected.annual_turnover || "—"} />
                <Detail k="Services" v={(selected.services || []).join(", ") || "—"} full />
                <Detail k="Trade lanes" v={selected.trade_lanes || "—"} full />
                <Detail k="About" v={selected.about || "—"} full />
                <Detail k="References" v={(selected.references || []).join(" · ") || "—"} full />
              </div>

              {selected.summary_for_admin && (
                <div className="bg-navy/5 border border-navy/10 p-4 text-sm text-navy">
                  <div className="overline mb-2">AI summary</div>
                  {selected.summary_for_admin}
                </div>
              )}
              {(selected.risk_flags?.length || selected.missing_information?.length) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  {selected.risk_flags?.length > 0 && (
                    <div className="border border-red-200 bg-red-50 p-3">
                      <div className="overline !text-red-700 mb-2">Risk flags</div>
                      <ul className="list-disc pl-5 text-red-700/90 space-y-1">
                        {selected.risk_flags.map((r, i) => <li key={i}>{r}</li>)}
                      </ul>
                    </div>
                  )}
                  {selected.missing_information?.length > 0 && (
                    <div className="border border-amber-200 bg-amber-50 p-3">
                      <div className="overline !text-amber-700 mb-2">Missing info</div>
                      <ul className="list-disc pl-5 text-amber-700/90 space-y-1">
                        {selected.missing_information.map((r, i) => <li key={i}>{r}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              <div className="flex flex-wrap gap-3 pt-3 border-t border-black/10">
                <button data-testid="application-process-button" disabled={busy} onClick={() => process(selected.id)} className="btn-secondary">
                  <Sparkle size={14} /> {busy ? "Processing…" : "Run AI review"}
                </button>
                <button data-testid="application-approve-button" disabled={busy || selected.status === "approved"} onClick={() => onboard(selected.id)} className="btn-primary bg-emerald-600 hover:bg-emerald-700 border-emerald-600">
                  <UserPlus size={14} /> Approve & onboard
                </button>
                <button data-testid="application-reject-button" disabled={busy || selected.status === "rejected"} onClick={() => setStatus(selected.id, "rejected")} className="btn-secondary border-red-500 text-red-600 hover:bg-red-600 hover:text-white">
                  <XCircle size={14} /> Reject
                </button>
                <button data-testid="application-review-button" disabled={busy} onClick={() => setStatus(selected.id, "reviewed")} className="btn-secondary">
                  <CheckCircle size={14} /> Mark reviewed
                </button>
              </div>

              {onboardOutput && (
                <div className="pt-4 border-t border-black/10">
                  <div className="overline mb-2">Onboarding package</div>
                  <AgentJsonBox data={onboardOutput} testId="onboarding-output" />
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

const Detail = ({ k, v, full }) => (
  <div className={full ? "col-span-2" : ""}>
    <div className="overline mb-0.5">{k}</div>
    <div className="text-navy/80">{String(v ?? "—")}</div>
  </div>
);
